import { useState, useEffect } from 'react';
import { Cookie, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function CookieBanner() {
  const [show, setShow] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Check if already answered
    const cookieChoice = localStorage.getItem('axeris_cookies_accepted');
    
    // Don't show on Emergent preview or dashboards
    const hostname = window.location.hostname;
    const pathname = window.location.pathname;
    
    // Skip on preview/dev environments
    const isPreviewOrDev = hostname.includes('localhost') || 
                           hostname.includes('127.0.0.1');
    const isDashboard = pathname.startsWith('/client') || pathname.startsWith('/admin');
    const isAuthPage = pathname.startsWith('/login') || pathname.startsWith('/reset') || pathname.startsWith('/auth');
    
    // Only show on production public site, first time
    if (cookieChoice === null && !isPreviewOrDev && !isDashboard && !isAuthPage) {
      // Show immediately, animate after
      setShow(true);
      requestAnimationFrame(() => setVisible(true));
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('axeris_cookies_accepted', 'true');
    setVisible(false);
    setTimeout(() => setShow(false), 300);
    
    // Load Google Analytics
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=G-LKEH9VFK7G';
    document.head.appendChild(script);
    
    window.dataLayer = window.dataLayer || [];
    function gtag(){window.dataLayer.push(arguments);}
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', 'G-LKEH9VFK7G');
  };

  const handleDecline = () => {
    localStorage.setItem('axeris_cookies_accepted', 'false');
    setVisible(false);
    setTimeout(() => setShow(false), 300);
  };

  if (!show) return null;

  return (
    <>
      {/* Backdrop - simple overlay, no blur */}
      <div 
        className={`fixed inset-0 bg-black/50 z-[200] transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`}
        data-testid="cookie-backdrop"
        onClick={handleDecline}
      />
      
      {/* Cookie Banner */}
      <div 
        className={`fixed inset-x-0 bottom-0 z-[201] p-4 md:p-6 transition-transform duration-300 ${visible ? 'translate-y-0' : 'translate-y-full'}`}
        data-testid="cookie-banner"
      >
        <div className="max-w-4xl mx-auto">
          <div className="bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-primary/10 via-purple-500/10 to-pink-500/10 px-6 py-4 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-primary/20">
                  <Cookie className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Nous utilisons des cookies</h3>
                  <p className="text-sm text-muted-foreground">Pour améliorer votre expérience</p>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6">
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Nous utilisons des cookies pour analyser le trafic de notre site et améliorer nos services. 
                Ces données sont anonymes et nous aident à vous offrir une meilleure expérience.
                En cliquant sur <span className="font-medium text-foreground">"Accepter"</span>, vous consentez à l'utilisation de ces cookies.
              </p>
              
              {/* Privacy features */}
              <div className="flex flex-wrap gap-4 mb-6">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Shield className="h-4 w-4 text-green-500" />
                  <span>Données anonymisées</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Shield className="h-4 w-4 text-green-500" />
                  <span>Aucune revente</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Shield className="h-4 w-4 text-green-500" />
                  <span>Conforme RGPD</span>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <button
                  onClick={handleAccept}
                  className="flex-1 sm:flex-none px-8 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-primary/25"
                  data-testid="cookie-accept-btn"
                >
                  Accepter
                </button>
                <button
                  onClick={handleDecline}
                  className="flex-1 sm:flex-none px-8 py-3 bg-muted hover:bg-muted/80 text-foreground font-medium rounded-xl transition-all duration-200"
                  data-testid="cookie-decline-btn"
                >
                  Refuser
                </button>
                <Link
                  to="/politique-confidentialite"
                  className="text-center sm:text-left text-sm text-primary hover:underline underline-offset-4 py-2 sm:ml-auto"
                >
                  Politique de confidentialité
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
